import React, { useState, useEffect, useRef } from 'react';
import { FilledButton } from './Button';
import { FaChevronDown } from "react-icons/fa6";
import { BoldChip, Chip } from './Chip';

type MultiDropdownMenuProps = {
    options: any[]; // List of possible values to select
    optionsToTextMapper?: (option: any) => string;
    elements: any[];
    setElements: (elements: any[]) => void;
    maxNumSelections: number,
    label?: string; // Optional label for the dropdown
    placeholder?: string;
};

const MultiDropdownMenu: React.FC<MultiDropdownMenuProps & { variant: "filled" | "outlined" }> = ({
    variant,
    options,
    optionsToTextMapper = undefined,
    elements,
    setElements,
    maxNumSelections,
    label,
    placeholder
}) => {
    const [isOpen, setIsOpen] = useState(false); // State to toggle dropdown visibility
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]); // State to track selected option
    const dropdownRef = useRef<HTMLDivElement>(null); // Ref to track the dropdown element

    const variantClasses = {
        filled: 'bg-white',
        outlined: `bg-white border border-gray-500 focus:ring-primary focus:border-primary hover:bg-gray-50
        ${isOpen
                ? 'border-primary bg-neutral-100'
                : ''}`,

    };

    const variantItemClasses = {
        filled: 'bg-white ',
        outlined: ` hover:bg-neutral-100`,
    };

    const selectedElementClasses = {
        filled: '',
        outlined: `bg-neutral-200`,
    }


    const isSelectedElement = (elem: any) => {
        if (optionsToTextMapper) {
            return selectedOptions.includes(optionsToTextMapper(elem))
        } else {
            return selectedOptions.includes(elem);
        }
    }

    const addElement = (elem: any) => {
        if (selectedOptions.length >= maxNumSelections) {
            return;
        }
        if (optionsToTextMapper) {
            const options = [...selectedOptions, optionsToTextMapper(elem)]
            setSelectedOptions(options)
        } else {
            const options = [...selectedOptions, elem]
            setSelectedOptions(options)
        }
        const newElements = [...elements, elem]
        setElements(newElements)
    }

    const removeElement = (elem: any) => {
        if (optionsToTextMapper) {

            const newElements = []
            const newOptions = []
            for (const e of elements) {
                if (optionsToTextMapper(e) !== optionsToTextMapper(elem)) {
                    newElements.push(e);
                    newOptions.push(optionsToTextMapper(e));
                } else {

                }
            }

            setElements(newElements)
            setSelectedOptions(newOptions);

        } else {
            const options = selectedOptions.filter(option => option !== elem);
            setSelectedOptions(options)
            const newElements = elements.filter((element: any) => element !== elem);
            setElements([...newElements])
        }
    }

    // if already selected, remove from selection, otherwise add
    const handleSelect = (option: any) => {
        if (optionsToTextMapper) {
            if (selectedOptions.includes(optionsToTextMapper(option))) {
                removeElement(option);
            } else {
                addElement(option);
            }
        } else {
            if (selectedOptions.includes(option)) {
                removeElement(option);
            } else {
                addElement(option);
            }

        }

    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);



    return (
        <div className="relative" ref={dropdownRef}>
            {/* Label */}
            {label && <label>{label}</label>}

            {/* Dropdown Button */}
            <div
                onClick={() => setIsOpen(!isOpen)}
                className={`flex flex-row items-center justify-between py-2 px-4 cursor-pointer rounded-md
                ${variant === 'filled' ?
                        variantClasses.filled :
                        variant === 'outlined' ?
                            variantClasses.outlined : ''
                    } 
                `}
            >
                <span className='text-neutral-900'>{placeholder !== undefined
                    ? placeholder
                    : `--Select up to ${maxNumSelections} options--`}</span>
                <FaChevronDown
                    className={`ml-2 w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
            </div>

            {/* Show selections as chip with delete button */}
            {elements.length > 0 && <div className='flex flex-wrap gap-2 mt-1'>
                {elements.map((element: any) => (
                    <div key={element}>
                        <BoldChip
                            theme='secondary'
                            onDelete={
                                () => removeElement(element)
                            }
                        >{optionsToTextMapper ? optionsToTextMapper(element) : element}</BoldChip>
                    </div>))}
            </div>}


            {/* Dropdown Menu */}
            {isOpen && (
                <div
                    className={`w-full absolute z-10 mt-2 rounded-lg shadow-lg max-h-56 overflow-auto transition-all duration-200 ease-in-out bg-white border border-neutral-200`}
                >
                    {options.map((option, index) => (
                        <div
                            key={index}
                            onClick={() => handleSelect(option)}
                            className={`m-2 px-6 py-2 rounded-md cursor-pointer transition-all duration-150 
                            ${variant === 'filled' ?
                                    variantItemClasses.filled :
                                    variant === 'outlined' ?
                                        variantItemClasses.outlined : ''}
                                        ${isSelectedElement(option)
                                    ? 'bg-neutral-200'
                                    : ''
                                }`
                            }>
                            {optionsToTextMapper
                                ? <span>{optionsToTextMapper(option)}</span>
                                : <span>{option}</span>
                            }
                        </div>
                    ))}
                </div>
            )
            }
        </div >
    );
};

export const FilledMultiDropdownMenu: React.FC<MultiDropdownMenuProps> = (props) => <MultiDropdownMenu {...props} variant="filled" />;
export const OutlinedMultiDropdownMenu: React.FC<MultiDropdownMenuProps> = (props) => <MultiDropdownMenu {...props} variant="outlined" />;

// export default MultiDropdownMenu;